(() => {
  'use strict';

  const STORAGE_KEY = 'wishlist:v1';
  const INIT_KEY = '__norkayaWishlistInit__';

  if (window[INIT_KEY]) return;
  window[INIT_KEY] = true;

  const SELECTORS = {
    button: '[data-wishlist-button]',
    count: '[data-wishlist-count]',
  };

  function readWishlist() {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return { items: [] };
    }

    try {
      const data = JSON.parse(raw);

      if (!data || !Array.isArray(data.items)) {
        return { items: [] };
      }

      data.items = data.items
        .filter(Boolean)
        .map((item) => {
          const handle =
            item.handle ||
            item.productHandle ||
            item.product_handle ||
            '';

          const url = item.url || '';
          const handleFromUrl = url ? String(url).split('?')[0].split('/products/')[1] || '' : '';
          const finalHandle = handle || handleFromUrl;

          return {
            key: finalHandle ? `h:${finalHandle}` : '',
            handle: finalHandle,
            url,
            title: item.title || '',
            image: item.image || '',
            price: item.price || '',
            compareAt: item.compareAt || '',
            available: typeof item.available === 'boolean' ? item.available : null,
            variantId: item.variantId || item.variant_id || '',
            updatedAt: item.updatedAt || 0,
          };
        })
        .filter((item) => item.handle);

      return data;
    } catch {
      return { items: [] };
    }
  }

  function writeWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items }));
    } catch (error) {
      console.warn('[wishlist] localStorage write failed', error);
    }
  }

  function getButtonKey(button) {
    const handle = button.dataset.productHandle || '';
    const url = button.dataset.productUrl || '';

    if (handle) {
      return `h:${handle}`;
    }

    if (url) {
      const handleFromUrl = String(url).split('?')[0].split('/products/')[1] || '';

      if (handleFromUrl) {
        return `h:${handleFromUrl}`;
      }
    }

    return '';
  }

  function buildItem(button) {
    const url = button.dataset.productUrl || '';
    const handle =
      button.dataset.productHandle ||
      (url ? String(url).split('?')[0].split('/products/')[1] || '' : '');

    if (!handle) return null;

    return {
      key: `h:${handle}`,
      handle,
      url,
      title: button.dataset.productTitle || '',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
      compareAt: button.dataset.productCompareAt || '',
      available: button.dataset.productAvailable === 'true',
      variantId: button.dataset.variantId || '',
      updatedAt: Date.now(),
    };
  }

  function hasItem(items, key) {
    return items.some((item) => item && item.key === key);
  }

  function render(root = document) {
    const { items } = readWishlist();
    const count = items.length;

    root.querySelectorAll(SELECTORS.count).forEach((countElement) => {
      countElement.textContent = String(count);
      countElement.hidden = count === 0;
    });

    root.querySelectorAll(SELECTORS.button).forEach((button) => {
      const key = getButtonKey(button);

      if (!key) return;

      const active = hasItem(items, key);

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function emit(items) {
    const detail = {
      count: items.length,
      items,
    };

    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail }));
    document.dispatchEvent(new CustomEvent('wishlist:update', { detail }));
  }

  function toggle(button) {
    const key = getButtonKey(button);

    if (!key) return;

    const { items } = readWishlist();
    let nextItems;
    let didAdd = false;

    if (hasItem(items, key)) {
      nextItems = items.filter((item) => item && item.key !== key);
    } else {
      const freshItem = buildItem(button);

      if (!freshItem) return;

      nextItems = items.filter((item) => item && item.handle !== freshItem.handle);
      nextItems = [freshItem, ...nextItems];
      didAdd = true;
    }

    writeWishlist(nextItems);
    render(document);
    emit(nextItems);

    const wishlistDialog = document.querySelector('wishlist-dialog');

    if (wishlistDialog?.openToast) {
      wishlistDialog.openToast(didAdd ? 'added' : 'removed', 2500);
    }
  }

  function handleClick(event) {
    const button = event.target.closest(SELECTORS.button);

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    toggle(button);
  }

  function observeThemeEditorReloads() {
    const observer = new MutationObserver((mutations) => {
      let shouldRender = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (
            node.matches?.(SELECTORS.count) ||
            node.matches?.(SELECTORS.button) ||
            node.querySelector?.(SELECTORS.count) ||
            node.querySelector?.(SELECTORS.button)
          ) {
            shouldRender = true;
          }
        });
      });

      if (shouldRender) {
        requestAnimationFrame(() => render(document));
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function syncProductWishlistButton(event) {
    const html = event.detail?.data?.html;

    if (!html) return;

    const root = event.target?.closest?.('.shopify-section, dialog') || document;
    const currentButtons = root.querySelectorAll('[data-product-wishlist-button]');

    if (!currentButtons.length) return;

    currentButtons.forEach((button) => {
      const productId = button.dataset.productId;
      const newButton = html.querySelector(
        `[data-product-wishlist-button][data-product-id="${productId}"]`
      );

      if (!newButton) return;

      [
        'variantId',
        'productUrl',
        'productAvailable',
        'productImage',
        'productPrice',
        'productCompareAt',
      ].forEach((key) => {
        if (newButton.dataset[key] != null && newButton.dataset[key] !== '') {
          button.dataset[key] = newButton.dataset[key];
        } else {
          delete button.dataset[key];
        }
      });
    });

    render(root);
  }

  function init() {
    document.addEventListener('click', handleClick, true);
    document.addEventListener('variant:update', syncProductWishlistButton);
    observeThemeEditorReloads();

    render(document);

    window.addEventListener('load', () => {
      render(document);
    });

    window.addEventListener('pageshow', () => {
      render(document);
    });

    document.addEventListener('shopify:section:load', (event) => {
      render(event.target || document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.norkayaWishlist = {
    read: readWishlist,
    count: () => readWishlist().items.length,
    items: () => readWishlist().items,
    sync: () => render(document),
    clear: () => {
      writeWishlist([]);
      render(document);
      emit([]);
    },
  };
})();